import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHero } from "@/components/PageHero";
import { Reveal, MaskLinesInView } from "@/components/Reveal";
import { EventForm } from "@/components/EventForm";
import { LuxButton } from "@/components/LuxButton";
import { Users, Sparkles, Box, X, Mail, FileText } from "lucide-react";
import { IMG, rooms, grandCombo, eventsCoordinator, offeringsPacket } from "@/data/brand";

const comparison = [
  { room: "Wright Room", seated: "Up to 22", reception: "Up to 40", ideal: "Meetings & Intimate Events" },
  { room: "Oak Park Room", seated: "Up to 64", reception: "Up to 75", ideal: "Dinners & Receptions" },
  { room: "Robie Room", seated: "Up to 32", reception: "Up to 40", ideal: "Celebrations" },
  { room: "Taliesin Room", seated: "Up to 14", reception: "—", ideal: "Intimate Private Dining" },
];

const categories = [
  { title: "Corporate Events", copy: "Boardroom dinners and full-floor conferences with dedicated AV and presentation capabilities." },
  { title: "Rehearsal Dinners", copy: "Warm, intimate settings to gather your closest company the night before." },
  { title: "Celebrations", copy: "Birthdays, anniversaries and milestones marked in cinematic style." },
  { title: "Holiday Parties", copy: "Seasonal menus and festive rooms for teams and families alike." },
  { title: "Receptions", copy: "Standing receptions for up to approximately 250 guests." },
];

const PrivateDiningPage = () => {
  const [tour, setTour] = useState(null); // { name, url }

  return (
    <main data-testid="private-dining-page">
      <PageHero kicker="Private Dining & Events" title={["Private Dining", "& Events"]} image={IMG.privateDining} />

      {/* Intro */}
      <section className="bg-[#f8f5f0] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#C1272D]">Occasions</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1a1817] font-light mt-5 leading-[1.05]">
              <MaskLinesInView lines={["A room for every", "occasion"]} />
            </h2>
            <p className="text-[#6d6661] font-light leading-relaxed mt-6 max-w-md">
              Our second floor offers distinctive private rooms with exceptional audio-visual to accommodate every gathering — from intimate dinners to receptions of up to approximately 250 guests.
            </p>
            <div className="mt-8">
              <LuxButton href={offeringsPacket} variant="outline-dark" testid="pd-packet">See Our Offerings</LuxButton>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            {categories.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="border-l border-[#C1272D]/40 pl-5">
                  <h3 className="font-serif text-2xl text-[#1a1817]">{c.title}</h3>
                  <p className="text-[#6d6661] text-sm font-light mt-2 leading-relaxed">{c.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section id="rooms" className="bg-[#0a0908] py-24 md:py-32 grain relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal className="text-center mb-16">
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">The Spaces</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light mt-5">Our Private Rooms</h2>
            <p className="text-white/50 font-light mt-4 max-w-xl mx-auto">Step inside — explore each room in an immersive 3D tour.</p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {rooms.map((room, i) => (
              <Reveal key={room.name} delay={(i % 2) * 0.12}>
                <div className="group border border-white/10 hover:border-[#c5a059]/40 transition-colors duration-500 h-full flex flex-col">
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img src={room.image} alt={room.name} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <span className="absolute top-4 left-4 text-[10px] tracking-[0.2em] uppercase text-white/80 bg-black/40 backdrop-blur px-3 py-1.5 border border-white/20">
                      {room.sqft.toLocaleString()} sq ft
                    </span>
                    {room.tour && (
                      <button onClick={() => setTour({ name: room.name, url: room.tour })} data-testid={`room-tour-${i}`}
                        className="absolute bottom-4 right-4 flex items-center gap-2 bg-[#c5a059] text-[#0a0908] px-4 py-2.5 text-[10px] font-semibold tracking-[0.2em] uppercase hover:bg-[#b08d4a] transition-colors">
                        <Box size={14} /> 3D Tour
                      </button>
                    )}
                  </div>
                  <div className="p-8 flex flex-col flex-1">
                    <h3 className="font-serif text-3xl text-white">{room.name}</h3>
                    <p className="text-white/50 text-sm font-light mt-3 leading-relaxed">{room.desc}</p>

                    <div className="flex flex-wrap gap-x-6 gap-y-3 mt-6">
                      {room.caps.map(([label, n]) => (
                        <div key={label} className="flex items-center gap-2 text-white/70">
                          {label === "Reception" ? <Sparkles size={15} className="text-[#c5a059]" /> : <Users size={15} className="text-[#c5a059]" />}
                          <span className="font-serif text-lg text-white">{n}</span>
                          <span className="text-[9px] tracking-[0.15em] uppercase text-white/40">{label}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-white/10">
                      {room.amenities.map((a) => (
                        <span key={a} className="text-[10px] tracking-[0.12em] uppercase text-white/60 border border-white/15 px-3 py-1.5">{a}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}

            {/* Grand combo — beside the Combined room */}
            <Reveal delay={0.12}>
              <div className="border border-[#c5a059]/40 bg-[#0f0d0c] h-full flex flex-col items-center justify-center text-center p-10 md:p-12">
                <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">The Full Floor</span>
                <h3 className="font-serif text-3xl md:text-4xl text-white font-light mt-4">{grandCombo.name}</h3>
                <p className="text-white/55 font-light mt-4 max-w-md leading-relaxed">{grandCombo.desc}</p>
                <p className="font-serif text-7xl md:text-8xl text-[#c5a059] mt-8 leading-none">{grandCombo.reception}</p>
                <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mt-2">Reception Capacity</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Find Your Space — quick comparison */}
      <section className="bg-[#f8f5f0] py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal className="text-center mb-14">
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#C1272D]">Room Comparison</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1a1817] font-light mt-5">Find Your Space</h2>
            <p className="text-[#6d6661] font-light mt-4 max-w-xl mx-auto">
              From intimate dinners to larger celebrations, find the River Stone space that fits your occasion.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {/* Desktop table */}
            <div className="hidden md:block border-t border-black/10">
              <div className="grid grid-cols-12 py-4 border-b border-black/10 text-[10px] tracking-[0.25em] uppercase text-[#6d6661]">
                <div className="col-span-4">Room</div>
                <div className="col-span-2 text-center">Seated</div>
                <div className="col-span-2 text-center">Reception</div>
                <div className="col-span-4">Ideal For</div>
              </div>
              {comparison.map((r) => (
                <div key={r.room} className="grid grid-cols-12 py-5 border-b border-black/10 items-center hover:bg-black/[0.02] transition-colors">
                  <div className="col-span-4 font-serif text-2xl text-[#1a1817]">{r.room}</div>
                  <div className="col-span-2 text-center font-serif text-xl text-[#C1272D]">{r.seated}</div>
                  <div className="col-span-2 text-center font-serif text-xl text-[#C1272D]">{r.reception}</div>
                  <div className="col-span-4 text-[#6d6661] text-sm font-light">{r.ideal}</div>
                </div>
              ))}
            </div>
            {/* Mobile stacked */}
            <div className="md:hidden space-y-4">
              {comparison.map((r) => (
                <div key={r.room} className="border border-black/10 bg-white p-6">
                  <h3 className="font-serif text-2xl text-[#1a1817]">{r.room}</h3>
                  <p className="text-[#6d6661] text-sm font-light mt-1">{r.ideal}</p>
                  <div className="flex gap-8 mt-4">
                    <div><span className="block font-serif text-xl text-[#C1272D]">{r.seated}</span><span className="text-[9px] tracking-[0.2em] uppercase text-[#6d6661]">Seated</span></div>
                    <div><span className="block font-serif text-xl text-[#C1272D]">{r.reception}</span><span className="text-[9px] tracking-[0.2em] uppercase text-[#6d6661]">Reception</span></div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="text-center mt-12">
              <p className="font-serif italic text-xl text-[#1a1817]">Planning something larger?</p>
              <p className="text-[#6d6661] font-light text-sm mt-2 max-w-lg mx-auto">
                Select rooms can be combined to accommodate larger events and receptions of up to approximately 250 guests.
              </p>
              <div className="mt-7">
                <LuxButton href="#events" variant="outline-dark" testid="pd-plan-event"
                  onClick={(e) => {
                    e.preventDefault();
                    const scroll = () => {
                      const el = document.getElementById("events");
                      if (el && window.lenis) window.lenis.scrollTo(el, { offset: -80 });
                      else el?.scrollIntoView({ behavior: "smooth" });
                    };
                    scroll();
                    setTimeout(scroll, 550);
                  }}>
                  Plan Your Event
                </LuxButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Coordinator + inquiry form */}
      <section id="events" className="bg-[#161412] py-24 md:py-32 grain relative">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Reveal className="text-center mb-12">
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">Event Inquiry</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-white font-light mt-5">Start Planning</h2>
            <p className="text-white/50 font-light mt-4 max-w-2xl mx-auto">
              Tell us about your occasion, or reach our Special Events Coordinator directly to schedule your next event.
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
              <a href={`mailto:${eventsCoordinator.email}`} data-testid="pd-coordinator"
                className="flex items-center gap-3 border border-[#c5a059]/40 bg-[#0f0d0c] px-6 py-4 hover:border-[#c5a059] transition-colors group">
                <Mail size={18} className="text-[#c5a059]" />
                <span className="text-left">
                  <span className="block text-white text-sm">{eventsCoordinator.name} · {eventsCoordinator.title}</span>
                  <span className="block text-[#c5a059] text-xs font-light group-hover:underline">{eventsCoordinator.email}</span>
                </span>
              </a>
              <a href={offeringsPacket} target="_blank" rel="noreferrer" data-testid="pd-packet-2"
                className="flex items-center gap-3 border border-white/15 px-6 py-4 text-white/70 hover:text-white hover:border-white/40 transition-colors text-xs tracking-[0.15em] uppercase">
                <FileText size={16} className="text-[#c5a059]" /> Offerings Packet
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12}><EventForm /></Reveal>
          <p className="text-white/30 text-xs font-light text-center mt-8 leading-relaxed">
            Food & beverage minimums / room charges apply. 72-hour advance booking required based on availability. Any group over 9 guests is seated in a private room.
          </p>
        </div>
      </section>

      {/* 3D Tour modal */}
      <AnimatePresence>
        {tour && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10" onClick={() => setTour(null)} data-testid="tour-modal">
            <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <p className="font-serif text-2xl text-white">{tour.name} <span className="text-[#c5a059] text-sm font-sans tracking-[0.2em] uppercase ml-3">3D Tour</span></p>
                <button onClick={() => setTour(null)} data-testid="tour-close" className="text-white/70 hover:text-white"><X size={26} /></button>
              </div>
              <div className="relative w-full aspect-video border border-white/15 bg-black">
                <iframe title={tour.name} src={`${tour.url}&play=1`} allow="fullscreen; xr-spatial-tracking" allowFullScreen className="absolute inset-0 w-full h-full" />
              </div>
              <p className="text-white/40 text-xs font-light mt-3 text-center">
                Tour not loading?{" "}
                <a href={tour.url} target="_blank" rel="noreferrer" className="text-[#c5a059] underline hover:text-[#b08d4a]">Open the 3D tour in a new tab</a>.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default PrivateDiningPage;
